import React from "react";
import { Radio, ExternalLink } from "lucide-react";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  press_releases: "COMMUNIQUÉ",
  news: "REVUE DE PRESSE",
  blog: "BLOG",
};

const SOURCE_ACCENTS = {
  press_releases: "text-[#FCA5A5] border-[#D42B1E]/40 bg-[#D42B1E]/15",
  news: "text-amber-200 border-amber-400/30 bg-amber-400/10",
  blog: "text-sky-200 border-sky-400/30 bg-sky-400/10",
};

function flatten(payload) {
  if (!payload) return [];
  const out = [];
  ["press_releases", "news", "blog"].forEach((kind) => {
    (payload[kind] || []).forEach((item) => {
      out.push({ ...item, kind });
    });
  });
  // Sort by ISO date desc, fall back to original order otherwise.
  out.sort((a, b) => (b.date_iso || "").localeCompare(a.date_iso || ""));
  return out;
}

export default function FortinetNewsTicker() {
  const [items, setItems] = React.useState([]);
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [sourceLabel, setSourceLabel] = React.useState("Fortinet · Blog Secure Networking");
  const [paused, setPaused] = React.useState(false);
  const [error, setError] = React.useState(false);

  const fetchNews = React.useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fortinet/news`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(flatten(data));
      setUpdatedAt(data.updated_at);
      if (data.source_label) setSourceLabel(data.source_label);
      setError(false);
    } catch (_e) {
      setError(true);
    }
  }, []);

  React.useEffect(() => {
    fetchNews();
    const id = setInterval(fetchNews, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNews]);

  if (!items.length && !error) return null;

  // Duplicate the list once so the marquee animation can loop seamlessly.
  const stream = items.length ? [...items, ...items] : [];

  return (
    <div
      className="relative z-10 mt-8 w-full overflow-hidden border-y border-white/10 bg-black/55 backdrop-blur"
      data-testid="fortinet-news-ticker"
    >
      <div className="flex items-stretch">
        {/* LIVE badge */}
        <div className="flex shrink-0 items-center gap-2 border-r border-white/10 bg-[#D42B1E] px-4 py-2.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
          </span>
          <Radio className="h-3.5 w-3.5 text-white" />
          <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-white">
            En direct
          </span>
        </div>

        {/* Source pill */}
        <div className="hidden shrink-0 items-center gap-2 border-r border-white/10 bg-white/[0.03] px-3 py-2.5 sm:flex">
          <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/50">
            {sourceLabel}
          </span>
        </div>

        {/* Scrolling marquee */}
        <div
          className="relative flex-1 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="flex whitespace-nowrap will-change-transform pythagore-ticker-track"
            style={{
              animationPlayState: paused ? "paused" : "running",
              animationDuration: `${Math.max(30, items.length * 4)}s`,
            }}
            data-testid="fortinet-news-stream"
          >
            {stream.map((it, idx) => (
              <a
                key={`${it.url}-${idx}`}
                href={it.url}
                target="_blank"
                rel="noreferrer noopener"
                className="group inline-flex items-center gap-3 px-5 py-2.5 text-sm text-white/80 transition-colors hover:text-white"
                data-testid={`fortinet-news-item-${idx % items.length}`}
              >
                <span
                  className={`shrink-0 rounded-sm border px-1.5 py-[2px] text-[9px] font-bold tracking-[0.14em] uppercase ${SOURCE_ACCENTS[it.kind]}`}
                >
                  {SOURCE_LABELS[it.kind]}
                </span>
                {it.date && (
                  <span className="shrink-0 font-mono text-[11px] text-white/45">
                    {it.date}
                  </span>
                )}
                {it.source && (
                  <span className="shrink-0 text-[11px] italic text-white/55">
                    · {it.source}
                  </span>
                )}
                <span className="truncate max-w-[55ch] sm:max-w-[80ch]">
                  {it.title}
                </span>
                <ExternalLink className="h-3 w-3 shrink-0 text-white/35 transition-colors group-hover:text-white/70" />
                <span className="mx-3 text-white/15">▍</span>
              </a>
            ))}
          </div>
        </div>

        {/* Update timestamp (desktop) */}
        {updatedAt && (
          <div className="hidden shrink-0 items-center gap-1.5 border-l border-white/10 bg-white/[0.03] px-3 py-2.5 lg:flex">
            <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">
              MAJ&nbsp;{new Date(updatedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
